// Import React elements
import { useMemo } from "react";



// Export a custom hook that builds dropdown options from link data
export const useMMMOptions = (links, selectedMaterialId, selectedMethodId) => {


    
    // Use useMemo so we only recompute when links change
    const materialOptions = useMemo(() => {

        // Create an object map to help remove duplicates
        const byId = {};

        // Loop over every link in the array
        (links || []).forEach((link) => {

            // Safely read the material id from the link
            const materialId = link?.material_id;

            // Skip if material id is missing or null
            if (materialId == null) return;

            // Normalize the id to a string so we avoid 1 vs "1" issues
            const key = String(materialId);

            // Only add the material if we have not seen this id before
            if (!byId[key]) {

                // Store a simple option object for this material
                byId[key] = {

                    // The material id
                    id: materialId,

                    // Use the material_name if present, otherwise fall back to a generic label
                    name: link?.material_name || `Material #${materialId}`

                };

            }

        });

        // Return the unique material options as an array
        return Object.values(byId);

    }, [links]);



    // Use useMemo to compute method options based on selected material
    const methodOptions = useMemo(() => {

        // If no material is selected, there are no method options
        if (!selectedMaterialId) return [];

        // Normalize the selected material id to a string
        const selectedMaterialKey = String(selectedMaterialId);

        // Create an object map to ensure unique methods
        const byId = {};

        // Loop over all links to find those with the selected material
        (links || []).forEach((link) => {

            // Normalize this link's material id to string
            const linkMaterialKey = String(link?.material_id ?? "");

            // Only consider links that match the selected material
            if (linkMaterialKey !== selectedMaterialKey) return;

            // Read the method id from the link
            const methodId = link?.method_id;

            // Skip if missing
            if (methodId == null) return;

            // Normalize the method id to string
            const methodKey = String(methodId);

            // Only add if we have not seen this method id yet
            if (!byId[methodKey]) {

                // Store the method option
                byId[methodKey] = {

                    // The method id
                    id: methodId,

                    // Use the method_name if present, otherwise fall back to a generic label
                    name: link?.method_name || `Method #${methodId}`

                };

            }

        });

        // Return unique method options
        return Object.values(byId);

    }, [links, selectedMaterialId]);



    // Use useMemo to compute mode options based on selected material and method
    const modeOptions = useMemo(() => {

        // If material or method is not selected, there are no mode options
        if (!selectedMaterialId || !selectedMethodId) return [];

        // Normalize selected material and method ids to strings
        const selectedMaterialKey = String(selectedMaterialId);
        const selectedMethodKey = String(selectedMethodId);

        // Create an object map to ensure unique modes
        const byId = {};

        // Loop over all links
        (links || []).forEach((link) => {

            // Normalize this link's material id to string
            const linkMaterialKey = String(link?.material_id ?? "");

            // Normalize this link's method id to string
            const linkMethodKey = String(link?.method_id ?? "");

            // Only consider links that match both selected material and selected method
            if (linkMaterialKey !== selectedMaterialKey) return;
            if (linkMethodKey !== selectedMethodKey) return;

            // Read the mode id from the link
            const modeId = link?.mode_id;

            // Skip if missing
            if (modeId == null) return;

            // Normalize mode id to string
            const modeKey = String(modeId);

            // Only add if this mode id has not been seen yet
            if (!byId[modeKey]) {

                // Store the mode option
                byId[modeKey] = {

                    // The mode id
                    id: modeId,

                    // Use the mode_name if present, otherwise fall back to a generic label
                    name: link?.mode_name || `Mode #${modeId}`

                };

            }

        });

        // Return unique mode options
        return Object.values(byId);

    }, [links, selectedMaterialId, selectedMethodId]);



    // Return the three option lists from the hook
    return { materialOptions, methodOptions, modeOptions };

};
